import { createClient } from '@supabase/supabase-js'

// Environment variable validation with graceful fallbacks
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

// Create Supabase client with graceful fallback
export const supabase = supabaseUrl && supabaseAnonKey 
  ? createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true
      },
      global: {
        headers: {
          'X-Client-Info': 'undressing-sauce-app'
        }
      }
    })
  : null;

// Database types
export interface WaitlistEntry {
  id: string
  email: string
  created_at: string
  source: string
  status: string
  metadata: Record<string, any>
}

export interface QuizResult {
  id: string
  email?: string
  profile_data: Record<string, any>
  answers: Record<string, any>
  created_at: string
  updated_at: string
  share_token: string
  metadata: Record<string, any>
}

// Enhanced error handling wrapper
const withErrorHandling = async <T>(
  operation: () => Promise<T>,
  operationName: string
): Promise<T> => {
  try {
    if (!supabase) {
      // Return mock data instead of throwing error
      console.warn(`Supabase not available for ${operationName} - using mock data`);
      throw new Error('Database temporarily unavailable');
    }
    return await operation();
  } catch (error) {
    console.error(`Supabase ${operationName} error:`, error);
    
    // Re-throw with more context
    if (error instanceof Error) {
      throw new Error(`${operationName} failed: ${error.message}`);
    }
    throw new Error(`${operationName} failed: Unknown error`);
  }
};

// Waitlist functions with enhanced error handling and fallbacks
export const waitlistService = {
  // Add email to waitlist
  async addToWaitlist(email: string, source: string = 'website', metadata: Record<string, any> = {}) {
    if (!supabase) {
      // Mock success for demo purposes when Supabase is not available
      console.log('Mock waitlist signup:', { email, source });
      return { id: 'mock-id', email, source, created_at: new Date().toISOString() };
    }

    return withErrorHandling(async () => {
      const { data, error } = await supabase!
        .from('waitlist')
        .insert([
          {
            email: email.toLowerCase().trim(),
            source,
            metadata
          }
        ])
        .select()
        .single()

      if (error) {
        // Check if it's a duplicate email error
        if (error.code === '23505') {
          throw new Error('This email is already on the waitlist!')
        }
        throw new Error(error.message)
      }

      return data
    }, 'addToWaitlist');
  },

  // Check if email exists in waitlist
  async checkEmailExists(email: string) {
    if (!supabase) {
      // Return false for demo purposes
      return null;
    }

    return withErrorHandling(async () => {
      const { data, error } = await supabase!
        .from('waitlist')
        .select('id, email, created_at')
        .eq('email', email.toLowerCase().trim())
        .single()

      if (error && error.code !== 'PGRST116') {
        throw new Error(error.message)
      }

      return data
    }, 'checkEmailExists');
  },

  // Get waitlist stats
  async getWaitlistStats() {
    if (!supabase) {
      // Return mock stats
      return { totalSignups: 1247 };
    }

    return withErrorHandling(async () => {
      const { count, error } = await supabase!
        .from('waitlist')
        .select('*', { count: 'exact', head: true })

      if (error) {
        throw new Error(error.message)
      }

      return { totalSignups: count || 0 }
    }, 'getWaitlistStats');
  },

  // Get recent signups (for admin purposes)
  async getRecentSignups(limit: number = 10) {
    if (!supabase) {
      return [];
    }

    return withErrorHandling(async () => {
      const { data, error } = await supabase!
        .from('waitlist')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit)

      if (error) {
        throw new Error(error.message)
      }

      return data
    }, 'getRecentSignups');
  }
}

// Quiz results functions with enhanced error handling and fallbacks
export const quizService = {
  // Save quiz results
  async saveQuizResult(
    profileData: Record<string, any>, 
    answers: Record<string, any>, 
    email?: string,
    metadata: Record<string, any> = {}
  ) {
    if (!supabase) {
      // Return mock result for demo purposes
      const mockResult = {
        id: 'mock-' + Date.now(),
        email: email?.toLowerCase().trim(),
        profile_data: profileData,
        answers,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        share_token: 'mock-token-' + Date.now(),
        metadata
      };
      console.log('Mock quiz result saved:', mockResult);
      return mockResult;
    }

    return withErrorHandling(async () => {
      const { data, error } = await supabase!
        .from('quiz_results')
        .insert([
          {
            email: email?.toLowerCase().trim(),
            profile_data: profileData,
            answers,
            metadata: {
              ...metadata,
              timestamp: new Date().toISOString(),
              referrer: typeof document !== 'undefined' ? document.referrer || 'direct' : 'unknown'
            }
          }
        ])
        .select()
        .single()

      if (error) {
        throw new Error(error.message)
      }

      return data
    }, 'saveQuizResult');
  },

  // Get quiz result by share token
  async getQuizResultByToken(shareToken: string) {
    if (!supabase) {
      throw new Error('Database not available');
    }

    return withErrorHandling(async () => {
      const { data, error } = await supabase!
        .from('quiz_results')
        .select('*')
        .eq('share_token', shareToken)
        .single()

      if (error) {
        throw new Error(error.message)
      }

      return data
    }, 'getQuizResultByToken');
  },

  // Get quiz results by email
  async getQuizResultsByEmail(email: string) {
    if (!supabase) {
      return [];
    }

    return withErrorHandling(async () => {
      const { data, error } = await supabase!
        .from('quiz_results')
        .select('*')
        .eq('email', email.toLowerCase().trim())
        .order('created_at', { ascending: false })

      if (error) {
        throw new Error(error.message)
      }

      return data
    }, 'getQuizResultsByEmail');
  },

  // Update quiz result with email
  async updateQuizResultEmail(id: string, email: string) {
    if (!supabase) {
      throw new Error('Database not available');
    }

    return withErrorHandling(async () => {
      const { data, error } = await supabase!
        .from('quiz_results')
        .update({ email: email.toLowerCase().trim() })
        .eq('id', id)
        .select()
        .single()

      if (error) {
        throw new Error(error.message)
      }

      return data
    }, 'updateQuizResultEmail');
  },

  // Generate downloadable report data
  generateReportData(quizResult: QuizResult) {
    const { profile_data, answers, created_at, share_token } = quizResult
    
    return {
      title: 'Your DrizzID™ Flavor Profile Report',
      generatedAt: new Date(created_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }),
      shareUrl: `${typeof window !== 'undefined' ? window.location.origin : ''}/share/${share_token}`,
      profile: profile_data,
      answers,
      summary: this.generateProfileSummary(profile_data),
      recommendations: this.generateRecommendations(profile_data)
    }
  },

  // Generate profile summary
  generateProfileSummary(profileData: Record<string, any>) {
    const { profileType, intensity, heat, complexity, adventure, primaryGoal } = profileData
    
    return {
      profileType,
      primaryGoal: primaryGoal || 'Balanced Experience',
      intensityLevel: this.getIntensityLabel(intensity),
      heatPreference: this.getHeatLabel(heat),
      flavorComplexity: this.getComplexityLabel(complexity),
      adventureScore: this.getAdventureLabel(adventure),
      topStrengths: this.getTopStrengths(profileData),
      personalityMatch: this.getPersonalityMatch(profileData)
    }
  },

  // Generate recommendations
  generateRecommendations(profileData: Record<string, any>) {
    const { beautyScore, wellnessScore, performanceScore, profileType } = profileData
    
    const recommendations = []
    
    if (beautyScore >= 70) {
      recommendations.push({
        category: 'Beauty Enhancement',
        score: beautyScore,
        items: ['Marine Collagen Fusion', 'Vitamin C Glow Blend', 'Hyaluronic Acid Boost'],
        description: 'Perfect for skin health and anti-aging benefits'
      })
    }
    
    if (wellnessScore >= 70) {
      recommendations.push({
        category: 'Wellness Support',
        score: wellnessScore,
        items: ['Adaptogen Stress Shield', 'Probiotic Gut Guardian', 'Turmeric Anti-Inflammatory'],
        description: 'Ideal for overall health and stress management'
      })
    }
    
    if (performanceScore >= 70) {
      recommendations.push({
        category: 'Performance Boost',
        score: performanceScore,
        items: ['Pre-Workout Power Surge', 'Focus Nootropic Blend', 'Recovery Muscle Repair'],
        description: 'Great for energy, focus, and athletic performance'
      })
    }
    
    return recommendations
  },

  // Helper functions for labels
  getIntensityLabel(score: number) {
    if (score >= 75) return 'High Intensity'
    if (score >= 50) return 'Medium Intensity'
    if (score >= 25) return 'Mild Intensity'
    return 'Gentle'
  },

  getHeatLabel(score: number) {
    if (score >= 75) return 'Fire Lover'
    if (score >= 50) return 'Heat Seeker'
    if (score >= 25) return 'Mild Heat'
    return 'Cool & Calm'
  },

  getComplexityLabel(score: number) {
    if (score >= 75) return 'Complex Connoisseur'
    if (score >= 50) return 'Flavor Explorer'
    if (score >= 25) return 'Balanced Palate'
    return 'Simple & Clean'
  },

  getAdventureLabel(score: number) {
    if (score >= 75) return 'Fearless Explorer'
    if (score >= 50) return 'Curious Taster'
    if (score >= 25) return 'Cautious Explorer'
    return 'Traditional Tastes'
  },

  getTopStrengths(profileData: Record<string, any>) {
    const scores = [
      { name: 'Intensity', value: profileData.intensity || 0 },
      { name: 'Heat Tolerance', value: profileData.heat || 0 },
      { name: 'Flavor Complexity', value: profileData.complexity || 0 },
      { name: 'Adventure Spirit', value: profileData.adventure || 0 }
    ]
    
    return scores
      .sort((a, b) => b.value - a.value)
      .slice(0, 2)
      .map(s => s.name)
  },

  getPersonalityMatch(profileData: Record<string, any>) {
    const { intensity = 0, heat = 0, adventure = 0 } = profileData
    const total = intensity + heat + adventure
    
    if (total > 225) return 'Bold & Fearless'
    if (total > 150) return 'Adventurous & Confident'
    if (total > 75) return 'Balanced & Curious'
    return 'Gentle & Thoughtful'
  }
}